import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import CheckboxFormField from "components/checkbox-form-field";
import { SignDocumentIcon } from "components/icons";
import ImageUpload from "components/image-upload";
import SignatureDesk from "components/signature-desk";

import { validatePhotoDimensions, validatePhotoFileSizes } from "./util";

type SignaturePickerProps = {
  open: boolean;
  isLoading?: boolean;
  hasInitialImage?: boolean;
  title?: string;
  signatureDeskTitle?: ReactNode;
  submitButtonText?: string;
  submitButtonName?: string;
  onClose: () => void;
  onSubmit?: (files: File[], uploadToEdocs?: boolean) => Promise<void> | void;
  enableHandSignatureOnly?: boolean;
  showUploadToEdocsCheckbox?: boolean;
  largeSize?: boolean;
  maxSizeLimit?: number;
  maxWidth?: number;
};

export type SignaturePickerValues = {
  photos: File[];
  uploadToEdocs?: boolean;
};

const DEFAULT_VALUES: SignaturePickerValues = {
  photos: [],
  uploadToEdocs: false
};

export default function SignaturePicker({
  open,
  isLoading,
  title = "Signature",
  signatureDeskTitle = "Signature Desk",
  submitButtonName = "Save",
  submitButtonText,
  hasInitialImage,
  enableHandSignatureOnly,
  showUploadToEdocsCheckbox,
  onClose,
  onSubmit,
  largeSize,
  maxSizeLimit = 1 * 1024 * 1024,
  maxWidth = 700
}: SignaturePickerProps) {
  const theme = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [step, setStep] = useState<"main" | "signature-desk">(
    enableHandSignatureOnly ? "signature-desk" : "main"
  );

  useEffect(() => {
    if (enableHandSignatureOnly) {
      setStep("signature-desk");
    }
  }, [enableHandSignatureOnly]);

  const [cropImage, setCropImage] = useState<boolean>(true);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    setIsBusy(false);
  }, [open]);

  const defaultValues: SignaturePickerValues = useMemo(() => {
    return merge({}, DEFAULT_VALUES);
  }, []);

  const formMethods = useForm<SignaturePickerValues>({
    mode: "onChange",
    defaultValues
  });

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
    }
  }, [open]);

  useEffect(() => {
    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods]);

  const handleSubmit: SubmitHandler<{
    photos: File[];
    uploadToEdocs?: boolean;
  }> = useCallback(
    async (values) => {
      setIsBusy(true);
      await onSubmit?.(values.photos, values.uploadToEdocs);
      formMethods.reset();
      onClose();
      setIsBusy(false);
    },
    [onSubmit, formMethods, onClose]
  );
  const handleDelete = useCallback(async () => {
    setIsBusy(true);
    await onSubmit?.([]);
    formMethods.reset();
    onClose();
    setIsBusy(false);
  }, [onClose, onSubmit, formMethods]);

  const handleClose = useCallback(
    (event: any, reason: string) => {
      if (reason === "backdropClick") {
        return;
      }
      onClose();
      setStep("main");
    },
    [onClose]
  );

  const photos = formMethods.watch("photos");

  useEffect(() => {
    if (!photos?.length) {
      setErrorMessage(null);
      return;
    }

    const sizeError = validatePhotoFileSizes(photos, maxSizeLimit);
    if (sizeError) {
      setErrorMessage(sizeError);
      return;
    }

    const firstPhoto = photos.at(0);
    if (!firstPhoto || !maxWidth) {
      setErrorMessage(null);
      return;
    }

    let isCancelled = false;

    (async () => {
      const dimensionError = await validatePhotoDimensions(
        firstPhoto,
        maxWidth
      );
      if (!isCancelled) {
        setErrorMessage(dimensionError);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [maxSizeLimit, maxWidth, photos]);

  return (
    <Dialog
      fullWidth={Boolean(largeSize)}
      maxWidth={largeSize ? "lg" : undefined}
      open={open}
      onClose={handleClose}
      sx={{
        width: largeSize ? "800px" : undefined,
        margin: "0 auto"
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          boxSizing: "border-box",
          width: "100%",
          py: 4,
          px: 2,

          zIndex: 1800
        }}
      >
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={onClose}
          sx={{ position: "absolute", top: 4, right: 4 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {step === "main" && (
          <>
            <Typography variant="h5" marginBottom={1} component="div">
              {title}
            </Typography>
            <Typography variant="body1" component="div">
              Draw a signature
            </Typography>
            <IconButton
              onClick={() => setStep("signature-desk")}
              size="large"
              sx={{
                backgroundColor: theme.palette.grey[100],
                mt: 2,
                p: 6
              }}
            >
              <SignDocumentIcon style={{ height: "24px", width: "24px" }} />
            </IconButton>
            <Divider
              sx={{
                width: 150,
                m: 2
              }}
            >
              OR
            </Divider>
            <FormProvider {...formMethods}>
              <ImageUpload
                name="photos"
                onRemove={handleDelete}
                hasInitialImage={hasInitialImage}
                cropImage={cropImage}
                setCropImage={setCropImage}
              />
            </FormProvider>
            {showUploadToEdocsCheckbox && (
              <Box mt={-1}>
                <FormControlLabel
                  label="Upload to E-Docs"
                  control={
                    <CheckboxFormField
                      name="uploadToEdocs"
                      control={formMethods.control}
                      testId="uploadToEdocs"
                      sx={{ ml: 0 }}
                    />
                  }
                />
              </Box>
            )}
            <LoadingButton
              variant="contained"
              size="large"
              type="submit"
              loading={isLoading}
              sx={{
                marginTop: 2
              }}
              disabled={Boolean(errorMessage)}
              onClick={formMethods.handleSubmit(handleSubmit)}
            >
              {submitButtonName}
            </LoadingButton>
          </>
        )}
        {step === "signature-desk" && (
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              minHeight: "300px"
            }}
          >
            <Box display="flex" alignItems="center" marginBottom={2}>
              {!enableHandSignatureOnly && (
                <IconButton
                  sx={{ marginRight: 1 }}
                  onClick={() => setStep("main")}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              <Typography variant="h5" component="div" fontWeight={700}>
                {signatureDeskTitle}
              </Typography>
            </Box>
            <Box>
              <SignatureDesk
                width={largeSize ? 700 : 400}
                height={largeSize ? 314 : 200}
                submitButtonText={submitButtonText}
                isSubmitButton={enableHandSignatureOnly}
                isLoading={isBusy || isLoading}
                beforeButtonSlot={
                  showUploadToEdocsCheckbox ? (
                    <FormControlLabel
                      label="Upload to E-Docs"
                      control={
                        <CheckboxFormField
                          name="uploadToEdocs"
                          control={formMethods.control}
                          testId="uploadToEdocs"
                          sx={{ ml: 0 }}
                        />
                      }
                    />
                  ) : undefined
                }
                onCapture={(file: File) => {
                  formMethods.setValue("photos", [file]);
                  if (enableHandSignatureOnly) {
                    formMethods.handleSubmit(handleSubmit)();
                  } else {
                    setStep("main");
                  }
                }}
              />
            </Box>
          </Box>
        )}
        {Boolean(errorMessage) && (
          <Alert severity="error" sx={{ mt: 2, mb: -1 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </Dialog>
  );
}
